/**
 * Sidebar Navigation Component
 *
 * Left sidebar with navigation items, badge counts, and collapse functionality.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md Section 1.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Eye,
  FolderOpen,
  BookOpen,
  FileEdit,
  // Sparkles, // TODO: Re-enable when AI Insights is implemented
  Settings,
  HelpCircle,
  Shield,
  MessageSquarePlus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badgeKey?: 'watchtower' | 'insights' | 'notifications';
}

interface BadgeCounts {
  watchtower?: number;
  insights?: number;
  notifications?: number;
}

interface SidebarProps {
  className?: string;
  activeCaseFileId?: string;
  badgeCounts?: BadgeCounts;
}

const mainNavItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { id: 'watchtower', label: 'Watchtower', icon: Eye, path: '/watchtower', badgeKey: 'watchtower' },
  { id: 'casefile', label: 'CaseFile', icon: FolderOpen, path: '/casefile' },
  { id: 'carelog', label: 'Care Log', icon: BookOpen, path: '/care-log' },
  { id: 'casemaker', label: 'CaseMaker', icon: FileEdit, path: '/casemaker' },
  // TODO: Re-enable when backend getAIInsights is implemented
  // { id: 'insights', label: 'AI Insights', icon: Sparkles, path: '/ai-insights', badgeKey: 'insights' },
];

const supportNavItems: NavItem[] = [
  { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  { id: 'feedback', label: 'Beta Feedback', icon: MessageSquarePlus, path: '/feedback' },
  { id: 'privacy', label: 'Data Privacy Policy', icon: Shield, path: '/privacy' },
  { id: 'help', label: 'Help & Resources', icon: HelpCircle, path: '/help' },
];

export function Sidebar({ className = '', activeCaseFileId, badgeCounts = {} }: SidebarProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Care Log is active when on /casefile/:id/carelogs
    if (path === '/care-log') {
      return location.pathname.includes('/carelogs');
    }
    // Watchtower is active when on /casefile/:id/deadlines
    if (path === '/watchtower') {
      return location.pathname.includes('/deadlines');
    }
    // CaseMaker is active when on /casefile/:id/casemaker
    if (path === '/casemaker') {
      return location.pathname.includes('/casemaker');
    }
    // CaseFile should only be active on /casefile or /casefile/:id,
    // not on sub-module routes like /casefile/:id/carelogs, /deadlines, /casemaker
    if (path === '/casefile') {
      const segments = location.pathname.split('/').filter(Boolean);
      // Match /casefile (1 segment) or /casefile/:id (2 segments)
      return segments[0] === 'casefile' && segments.length <= 2;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: NavItem) => {
    // CaseFile-scoped modules require an activeCaseFileId
    const caseFileModules = ['casefile', 'carelog', 'watchtower', 'casemaker'];

    if (caseFileModules.includes(item.id)) {
      if (!activeCaseFileId) {
        // No CaseFile selected - navigate to dashboard
        navigate('/');
        return;
      }

      const routeMap: Record<string, string> = {
        casefile: `/casefile/${activeCaseFileId}`,
        carelog: `/casefile/${activeCaseFileId}/carelogs`,
        watchtower: `/casefile/${activeCaseFileId}/deadlines`,
        casemaker: `/casefile/${activeCaseFileId}/casemaker`,
      };

      navigate(routeMap[item.id]);
    } else {
      navigate(item.path);
    }
  };

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] ?? 0 : 0;

    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item)}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
          transition-all duration-250 group relative
          ${active
            ? 'bg-evergreen/10 text-evergreen'
            : 'text-slate-ink hover:bg-soft-neutral hover:text-ink'
          }
          ${isCollapsed ? 'justify-center' : ''}
        `}
        title={isCollapsed ? item.label : undefined}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-evergreen' : ''}`} />

        {!isCollapsed && (
          <>
            <span className="flex-1 text-left font-medium text-sm">{item.label}</span>

            {badgeCount > 0 && (
              <span className="badge badge-primary text-xs min-w-[20px] text-center">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </>
        )}

        {isCollapsed && badgeCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-evergreen rounded-full" />
        )}
      </button>
    );
  };

  return (
    <aside
      className={`
        flex flex-col bg-white border-r border-soft-neutral
        transition-all duration-350
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${className}
      `}
    >
      {/* Logo */}
      <div className={`
        flex items-center border-b border-soft-neutral
        ${isCollapsed ? 'justify-center px-2 py-4' : 'px-4 py-4'}
      `}>
        {isCollapsed ? (
          <div className="w-8 h-8 bg-evergreen rounded-lg flex items-center justify-center">
            <span className="text-white font-display font-semibold text-lg">H</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-evergreen rounded-lg flex items-center justify-center">
              <span className="text-white font-display font-semibold text-lg">H</span>
            </div>
            <span className="font-display text-xl text-deep-forest font-light">Hesvara</span>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div className="space-y-1">
          {!isCollapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-muted uppercase tracking-wider">
              Main
            </p>
          )}
          {mainNavItems.map(renderNavItem)}
        </div>

        {/* Divider */}
        <div className="my-4 border-t border-soft-neutral" />

        {/* Support Navigation */}
        <div className="space-y-1">
          {!isCollapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-muted uppercase tracking-wider">
              Support
            </p>
          )}
          {supportNavItems.map(renderNavItem)}
        </div>
      </nav>

      {/* Collapse Toggle */}
      <div className="border-t border-soft-neutral p-3">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
            text-muted hover:text-ink hover:bg-soft-neutral
            transition-all duration-250
            ${isCollapsed ? 'justify-center' : ''}
          `}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
